import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, setDoc, updateDoc, arrayUnion, deleteDoc, getDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { Loader2, AlertCircle, CheckCircle, ArrowLeft, Trophy, ArrowRight, Database } from 'lucide-react';

const TournamentMigrationTool = ({ currentClubId, clubs = [], onBack }) => {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  
  const [sourceClubId, setSourceClubId] = useState('');
  const [sourceTournaments, setSourceTournaments] = useState([]);
  const [selectedTournamentId, setSelectedTournamentId] = useState('');

  // Fetch all tournaments for the selected source club
  useEffect(() => {
    if (!sourceClubId) {
      setSourceTournaments([]);
      return;
    }

    const fetchTournaments = async () => {
      try {
        setLoading(true);
        const tQuery = query(collection(db, 'tournaments'), where('clubId', '==', sourceClubId));
        const tSnap = await getDocs(tQuery);
        const list = tSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setSourceTournaments(list);
      } catch (err) {
        console.error('Failed to load tournaments:', err);
        setError('Failed to load tournaments for the selected club.');
      } finally {
        setLoading(false);
      }
    };

    fetchTournaments();
  }, [sourceClubId]);

  const runMigration = async () => {
    if (!sourceClubId) {
      setError('Please select a source club from the dropdown first.');
      return;
    }
    if (!selectedTournamentId) {
      setError('Please select a tournament to duplicate.');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const sourceTournament = sourceTournaments.find(t => t.id === selectedTournamentId);
      if (!sourceTournament) {
        throw new Error('Selected tournament not found.');
      }

      const oldClubId = sourceClubId;
      const oldTournamentId = selectedTournamentId;
      const tournamentName = sourceTournament.name;
      const targetTournamentName = `${tournamentName} (Migrated)`;

      // Identify tournament mode
      const isNACC = tournamentName.includes('NACC');
      const isPSCC = tournamentName.includes('PSCC');

      // 1. Clone Tournament (IDEMPOTENT CHECK)
      setStatus('Checking for existing Tournament clone...');
      const checkTQuery = query(
        collection(db, 'tournaments'), 
        where('clubId', '==', currentClubId), 
        where('name', '==', targetTournamentName)
      );
      const checkTSnap = await getDocs(checkTQuery);
      
      let newTournamentId;
      if (!checkTSnap.empty) {
        newTournamentId = checkTSnap.docs[0].id;
        setStatus('Tournament clone already exists, skipping tournament creation...');
      } else {
        setStatus('Cloning Tournament document...');
        const newTournamentRef = doc(collection(db, 'tournaments'));
        newTournamentId = newTournamentRef.id;
        
        // Clone tournament metadata
        await setDoc(newTournamentRef, {
          ...sourceTournament,
          id: newTournamentId,
          clubId: currentClubId,
          name: targetTournamentName,
          createdAt: new Date().toISOString()
        });
      }

      // 2. Find and Clone Games
      setStatus('Syncing Games...');
      const gQuery = query(
        collection(db, 'games'), 
        where('clubId', '==', oldClubId),
        where('tournamentId', '==', oldTournamentId)
      );
      const gSnap = await getDocs(gQuery);
      
      const gameIdMap = {}; // Maps old game ID -> new game ID

      // Fetch existing target games to prevent duplicates
      const checkGQuery = query(
        collection(db, 'games'), 
        where('clubId', '==', currentClubId), 
        where('tournamentId', '==', newTournamentId)
      );
      const existingGSnap = await getDocs(checkGQuery);
      const existingGameNames = {};
      existingGSnap.forEach(doc => { existingGameNames[doc.data().name] = doc.id; });

      let gamesCloned = 0;
      for (const gameDoc of gSnap.docs) {
        const gameData = gameDoc.data();
        
        // Skip clone if a game with the identical name is already duplicated
        if (existingGameNames[gameData.name]) {
            gameIdMap[gameDoc.id] = existingGameNames[gameData.name];
            continue; 
        }

        // Apply rebranding rules to text fields in the game data
        const newGameRef = doc(collection(db, 'games'));
        let updatedGameData = { ...gameData };

        if (isNACC) {
          const ravensRegex = /Cascadia Ravens NACC T20|Cascadia Ravens NACC/gi;
          const yorkasRegex = /SUY Southern Yorkas|Seattle United Yorkas/gi;
          const rclRegex = /SUY RCL|Seattle United RCL/gi;

          const stringFields = ['homeTeam', 'awayTeam', 'homeTeamName', 'awayTeamName', 'teamName', 'opponent', 'title', 'name'];
          stringFields.forEach(field => {
            if (updatedGameData[field] && typeof updatedGameData[field] === 'string') {
              let str = updatedGameData[field];
              if (str.includes('Cascadia Ravens NACC')) {
                str = str.replace(ravensRegex, 'CWC NACC T20');
              }
              str = str.replace(yorkasRegex, 'Cascadia Ravens');
              str = str.replace(rclRegex, 'Cascadia Eagles');
              updatedGameData[field] = str;
            }
          });
        } else if (isPSCC) {
          const psccRegex = /Seattle United Yorkas PSCC T30/g;
          const stringFields = ['homeTeam', 'awayTeam', 'homeTeamName', 'awayTeamName', 'teamName', 'opponent', 'title', 'name'];
          stringFields.forEach(field => {
            if (updatedGameData[field] && typeof updatedGameData[field] === 'string') {
              updatedGameData[field] = updatedGameData[field].replace(psccRegex, 'CWC PSCC T30');
            }
          });
        }

        await setDoc(newGameRef, {
          ...updatedGameData,
          id: newGameRef.id,
          clubId: currentClubId,
          tournamentId: newTournamentId
        });
        
        gameIdMap[gameDoc.id] = newGameRef.id;
        gamesCloned++;
      }

      // 3. Find and Clone Expenses recursively linked to those Games
      setStatus('Syncing Financials & Expenses...');
      const eQuery = query(
        collection(db, 'expenses'), 
        where('clubId', '==', oldClubId),
        where('tournamentId', '==', oldTournamentId)
      );
      const eSnap = await getDocs(eQuery);

      const checkEQuery = query(
        collection(db, 'expenses'), 
        where('clubId', '==', currentClubId), 
        where('tournamentId', '==', newTournamentId)
      );
      const existingESnap = await getDocs(checkEQuery);
      const existingExpenseDesc = {};
      existingESnap.forEach(doc => { 
        existingExpenseDesc[doc.data().description || doc.data().type] = doc.id; 
      });

      let expensesCloned = 0;
      for (const expenseDoc of eSnap.docs) {
        const expenseData = expenseDoc.data();
        if (existingExpenseDesc[expenseData.description || expenseData.type]) {
            continue; // Skip if similar expense already cloned
        }

        const newExpenseRef = doc(collection(db, 'expenses'));
        await setDoc(newExpenseRef, {
          ...expenseData,
          id: newExpenseRef.id,
          clubId: currentClubId,
          tournamentId: newTournamentId,
          gameId: expenseData.gameId ? (gameIdMap[expenseData.gameId] || expenseData.gameId) : null
        });
        expensesCloned++;
      }

      // 4. Migrate Players to New Club (IF REQUESTED)
      let playersMigrated = 0;
      if (isNACC) {
        setStatus('Migrating Player Rosters to CWC...');
        const pQuery = query(collection(db, 'users'), where('joinedClubsIds', 'array-contains', oldClubId));
        const pSnap = await getDocs(pQuery);
        
        for (const playerDoc of pSnap.docs) {
            const pData = playerDoc.data();
            // If they already joined the new club, skip
            if (pData.joinedClubsIds && pData.joinedClubsIds.includes(currentClubId)) {
                continue;
            }
            
            const userRef = doc(db, 'users', playerDoc.id);
            const oldClubData = pData.joinedClubs?.[oldClubId] || { role: 'player', status: 'active' };
            
            await updateDoc(userRef, {
                joinedClubsIds: arrayUnion(currentClubId),
                [`joinedClubs.${currentClubId}`]: {
                    ...oldClubData,
                    clubName: 'Cascadia Willow Club',
                    joinedAt: new Date().toISOString()
                }
            });
            playersMigrated++;
        }
      } else {
        setStatus('Skipping player migration (players not in CWC will display as Unknown Player)...');
      }

      // 5. Rebrand Teams in CWC
      let teamsRebranded = 0;
      if (isNACC || isPSCC) {
        setStatus('Rebranding Teams in destination club settings...');
        const tSnap = await getDocs(query(collection(db, 'teams'), where('clubId', '==', currentClubId)));
        
        for (const docSnap of tSnap.docs) {
          const teamData = docSnap.data();
          if (!teamData.name) continue;
          
          if (isNACC) {
            if (teamData.name.includes('Cascadia Ravens NACC')) {
              await updateDoc(doc(db, 'teams', docSnap.id), { name: 'CWC NACC T20' });
              teamsRebranded++;
            } else if (teamData.name.includes('Yorkas')) {
              await updateDoc(doc(db, 'teams', docSnap.id), { name: 'Cascadia Ravens' });
              teamsRebranded++;
            } else if (teamData.name.includes('RCL')) {
              await updateDoc(doc(db, 'teams', docSnap.id), { name: 'Cascadia Eagles' });
              teamsRebranded++;
            }
          } else if (isPSCC) {
            if (teamData.name.includes('Seattle United Yorkas PSCC T30')) {
              await updateDoc(doc(db, 'teams', docSnap.id), { name: 'CWC PSCC T30' });
              teamsRebranded++;
            }
          }
        }
      }

      const rebrandMsg = teamsRebranded > 0 ? ` and rebranded ${teamsRebranded} teams` : '';
      setStatus(`Migration Complete! Cloned ${gamesCloned} games, ${expensesCloned} expenses${rebrandMsg}. Players not in CWC will successfully show up as "Unknown Player".`);
      
    } catch (err) {
      console.error(err);
      setError(err.message || 'An error occurred during migration.');
    } finally {
      setLoading(false);
    }
  };

  const wipeClones = async () => {
    if (!selectedTournamentId) {
      setError('Please select a tournament to reset first.');
      return;
    }

    const sourceTournament = sourceTournaments.find(t => t.id === selectedTournamentId);
    const targetName = `${sourceTournament?.name} (Migrated)`;

    if (!confirm(`Are you absolutely sure? This will permanently delete tournament "${targetName}" and all of its cloned games and expenses from CWC to let you start fresh!`)) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      setStatus('Locating cloned tournament documents...');
      
      const tQuery = query(
        collection(db, 'tournaments'), 
        where('clubId', '==', currentClubId), 
        where('name', '==', targetName)
      );
      const tSnap = await getDocs(tQuery);
      
      let purgeCount = 0;
      for (const tDoc of tSnap.docs) {
         const tid = tDoc.id;
         
         // Purge games
         const gSnap = await getDocs(query(collection(db, 'games'), where('clubId', '==', currentClubId), where('tournamentId', '==', tid)));
         for (const g of gSnap.docs) { 
           await deleteDoc(doc(db, 'games', g.id)).catch(() => {}); 
         }
         
         // Purge expenses
         const eSnap = await getDocs(query(collection(db, 'expenses'), where('clubId', '==', currentClubId), where('tournamentId', '==', tid)));
         for (const e of eSnap.docs) { 
           await deleteDoc(doc(db, 'expenses', e.id)).catch(() => {}); 
         }
         
         await deleteDoc(doc(db, 'tournaments', tid)).catch(() => {});
         purgeCount++;
      }
      
      setStatus(`Successfully deleted cloned tournament "${targetName}" and associated resources. You can now execute a fresh migration.`);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to wipe cloned data.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <button onClick={onBack} className="mb-6 flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors">
        <ArrowLeft size={20} /> Back to Dashboard
      </button>

      <div className="bg-white rounded-xl shadow-lg border border-purple-200 overflow-hidden mb-6">
        <div className="p-6 bg-purple-600 border-b border-purple-700 text-white flex items-center gap-3">
          <Database size={24} />
          <div>
            <h2 className="text-2xl font-bold">General Tournament Migration Tool</h2>
            <p className="opacity-95 text-sm mt-0.5">Migrate tournaments, games, and financial expenses from legacy clubs into CWC.</p>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 text-red-700 p-4 rounded-lg flex items-center gap-2 border border-red-100 text-sm">
              <AlertCircle size={18} className="shrink-0" />
              {error}
            </div>
          )}

          {status && !error && (
            <div className="bg-blue-50 text-blue-700 p-4 rounded-lg flex items-center gap-2 border border-blue-100 text-sm">
              {loading ? <Loader2 className="animate-spin shrink-0" size={18} /> : <CheckCircle size={18} className="shrink-0" />}
              {status}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Source Configuration */}
            <div className="bg-gray-50 p-5 rounded-lg border border-gray-200 space-y-4">
              <h3 className="font-bold text-gray-800 text-sm uppercase tracking-wider">Source Details</h3>
              
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Source Club</label>
                <select 
                  value={sourceClubId} 
                  onChange={(e) => { setSourceClubId(e.target.value); setSelectedTournamentId(''); }} 
                  className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 bg-white text-sm"
                  disabled={loading}
                >
                  <option value="">-- Select Origin Club --</option>
                  {clubs.sort((a,b) => a.name.localeCompare(b.name)).map(c => (
                     <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Tournament to Migrate</label>
                <select 
                  value={selectedTournamentId} 
                  onChange={(e) => setSelectedTournamentId(e.target.value)} 
                  disabled={!sourceClubId || loading} 
                  className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 bg-white text-sm disabled:opacity-50"
                >
                  <option value="">-- Select Tournament --</option>
                  {sourceTournaments.map(t => (
                     <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Destination Configuration */}
            <div className="bg-purple-50/40 p-5 rounded-lg border border-purple-100 flex flex-col justify-between">
              <div className="space-y-4">
                <h3 className="font-bold text-purple-900 text-sm uppercase tracking-wider">Destination details</h3>
                
                <div>
                  <label className="block text-xs font-semibold text-purple-700 mb-1">Target Club</label>
                  <div className="p-3 bg-white border border-purple-200 rounded-lg text-sm font-semibold text-purple-900">
                    Cascadia Willow Club (CWC)
                  </div>
                </div>

                {selectedTournamentId && (() => {
                  const t = sourceTournaments.find(x => x.id === selectedTournamentId);
                  const isNACC = t?.name.includes('NACC');
                  const isPSCC = t?.name.includes('PSCC');
                  
                  return (
                    <div className="p-3 bg-white border border-purple-200 rounded-lg text-xs text-gray-600 space-y-1">
                      <span className="font-bold text-purple-800">Selected Migration Mode:</span>
                      {isNACC && (
                        <p>✓ Will rebrand SUY/RCL teams to Ravens/Eagles.<br/>✓ Will auto-migrate SUY players to CWC.</p>
                      )}
                      {isPSCC && (
                        <p>✓ Will rebrand "Seattle United Yorkas PSCC T30" to "CWC PSCC T30".<br/>✓ Players not in CWC will default to "Unknown Player" (no auto-migration).</p>
                      )}
                      {!isNACC && !isPSCC && (
                        <p>✓ Will duplicate tournament, games, and expenses with no player onboarding or text rebranding.</p>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex gap-3 pt-4 border-t">
            <button
              onClick={wipeClones}
              disabled={loading || !selectedTournamentId}
              className="bg-red-50 hover:bg-red-100 text-red-700 font-bold py-3 px-6 rounded-lg transition-colors border border-red-200 disabled:opacity-40 text-sm flex-1"
            >
              Reset / Wipe Clones
            </button>
            <button
              onClick={runMigration}
              disabled={loading || !selectedTournamentId}
              className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-8 rounded-lg shadow-md transition-colors disabled:opacity-40 text-sm flex-[2]"
            >
              {loading ? 'Processing Migration...' : 'Execute Tournament Migration'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TournamentMigrationTool;
